// Plan 04b-03 / DRC-07 — Cadence-derived date defaults for LaunchProgramModal.
// Pure helpers. No React imports. Implements CONTEXT D3 (locked spec).

export type Cadence = 'Annual' | 'Quarterly' | 'Monthly' | 'Ad-hoc';

/** Format a Date as YYYY-MM-DD (HTML date input format, local time). */
function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * First day of the current quarter, given a reference date.
 * Q1=Jan, Q2=Apr, Q3=Jul, Q4=Oct.
 */
function firstDayOfQuarter(ref: Date): Date {
  const month = ref.getMonth(); // 0..11
  const quarterStart = Math.floor(month / 3) * 3; // 0, 3, 6, 9
  return new Date(ref.getFullYear(), quarterStart, 1);
}

/**
 * Per CONTEXT D3 — start-date defaults derived from cadence.
 * `now` is injectable for unit-testing; defaults to `new Date()`.
 */
export function defaultStartDate(
  cadence: Cadence | undefined,
  now: Date = new Date(),
): string {
  switch (cadence) {
    case 'Annual':
      return toIsoDate(new Date(now.getFullYear(), 0, 1));
    case 'Quarterly':
      return toIsoDate(firstDayOfQuarter(now));
    case 'Monthly':
      return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
    case 'Ad-hoc':
    default:
      return toIsoDate(now);
  }
}

/**
 * Per CONTEXT D3 — due-date offset from start_date by cadence.
 * Parses YYYY-MM-DD strictly to avoid TZ drift.
 */
export function defaultDueDate(
  cadence: Cadence | undefined,
  startDateIso: string,
): string {
  const [y, m, d] = startDateIso.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  const offsetDays =
    cadence === 'Annual'
      ? 365
      : cadence === 'Quarterly'
        ? 90
        : cadence === 'Monthly'
          ? 30
          : 14; // Ad-hoc + default
  const due = new Date(start);
  due.setDate(due.getDate() + offsetDays);
  return toIsoDate(due);
}
